/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';

import {
  GetElasticIndicesResponse,
  GetElserSupportResponse,
  GetIndexMappingResponse,
  ListDocsResponse,
  CreateElserResponse,
  IngestDocResponse
} from '../../../ts/elastic';
import { endpoints } from '../../ia/util/config';

export const getElasticIndices = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  port?: string
): Promise<GetElasticIndicesResponse> => {
  try {
    const response = await axios.post(
      `${endpoints.store}/elastic-search/indices`,
      {
        skillId,
        assistantId,
        url,
        authToken,
        port
      },
      {
        headers: { 'content-type': 'application/json' }
      }
    );

    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const createElser = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  index: string,
  port?: string
): Promise<CreateElserResponse> => {
  const response = await axios.post(
    `${endpoints.store}/elastic-search/setup-elser`,
    {
      skillId,
      assistantId,
      url,
      authToken,
      port,
      index
    },
    {
      headers: { 'content-type': 'application/json' }
    }
  );
  return response.data;
};

export const getElserSupport = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  port?: string
): Promise<GetElserSupportResponse> => {
  const response = await axios.post(
    `${endpoints.store}/elastic-search/elser-supported`,
    {
      skillId,
      assistantId,
      url,
      authToken,
      port
    },
    {
      headers: { 'content-type': 'application/json' }
    }
  );

  return response.data;
};

export const getIndexMapping = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  index: string,
  port?: string
): Promise<GetIndexMappingResponse> => {
  const response = await axios.post(
    `${endpoints.store}/elastic-search/mapping`,
    {
      skillId,
      assistantId,
      url,
      authToken,
      port,
      index
    },
    {
      headers: { 'content-type': 'application/json' }
    }
  );
  return response.data;
};

export const ingestDocs = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  index: string,
  files: File[],
  port?: string
): Promise<IngestDocResponse> => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('elastic-files', file);
  });
  formData.append('skillId', skillId);
  formData.append('assistantId', assistantId);
  formData.append('url', url);
  formData.append('authToken', authToken);
  formData.append('port', port);
  formData.append('index', index);

  const response = await axios.post(
    `${endpoints.store}/elastic-search/ingest-docs`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );

  return response.data;
};

export const deleteDocs = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  index: string,
  docIds: string[],
  port?: string
): Promise<ListDocsResponse> => {
  const response = await axios.post(
    `${endpoints.store}/elastic-search/delete-docs`,
    { docIds, skillId, assistantId, url, authToken, port, index },
    {
      headers: { 'content-type': 'application/json' }
    }
  );
  return response.data;
};

export const listDocs = async (
  skillId: string,
  assistantId: string,
  url: string,
  authToken: string,
  index: string,
  port?: string
): Promise<ListDocsResponse> => {
  try {
    const response = await axios.post(
      `${endpoints.store}/elastic-search/list-docs`,
      { skillId, assistantId, url, authToken, port, index },
      {
        headers: { 'content-type': 'application/json' }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
